import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import UsersTable from '../components/users/UsersTable';
import { useAdminAuth } from '../auth/AdminAuthContext';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';
import InviteUserModal from '../components/users/InviteUserModal.jsx';
import EditUserModal from '../components/users/EditUserModal.jsx';
import DeactivateUserModal from '../components/users/DeactivateUserModal.jsx';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { useCurrentUser } from '../auth/CurrentUserContext';
import { can, roleOptions } from '../auth/permissions';
import FilterFieldSelect from '../components/forms/FilterFieldSelect';
import LoadingView from '../../shared/components/LoadingView';
import buildAdminUsersQuery from '../hooks/buildAdminUsersQuery';
import { API_URL } from '../../config/env';

// 'all' is this page's filter sentinel, not a role; the real roles come from permissions.js.
const ROLE_OPTIONS = [{ value: 'all', label: 'All roles' }, ...roleOptions()];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'deactivated', label: 'Deactivated' },
];

export default function UsersPage() {
  const { accessToken } = useAdminAuth();
  const { user: currentUser } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [toggleUser, setToggleUser] = useState(null);
  const [toggleAction, setToggleAction] = useState('deactivate');
  
  const apiBaseUrl = `${API_URL}/v1/admin/users`;
  const searchParamsKey = searchParams.toString();
  const role = searchParams.get('role') || 'all';
  const status = searchParams.get('status') || 'all';

  useDocumentTitle('Users – Maintella');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = buildAdminUsersQuery(searchParamsKey);
      const endpoint = query ? `${apiBaseUrl}?${query}` : apiBaseUrl;
      const res = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error(err);
      setError('Could not load users.');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, accessToken, searchParamsKey]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openDeactivate = (user) => {
    setToggleUser(user);
    setToggleAction('deactivate');
  };

  const openReactivate = (user) => {
    setToggleUser(user);
    setToggleAction('reactivate');
  };

  const handleResendInvite = async (user) => {
    try {
      const res = await fetchWithAuth(`${apiBaseUrl}/${user.id}/resend_invitation`, { method: 'POST' });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || payload.errors?.join(', ') || 'Failed to resend invitation');
      }
      await fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to resend invitation');
    }
  };

  const canInvite = can(currentUser, 'users.invite');

  const setRoleFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('role');
    else next.set('role', value);
    setSearchParams(next, { replace: true });
  };

  const setStatusFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('status');
    else next.set('status', value);
    setSearchParams(next, { replace: true });
  };

  const filterExtras = useMemo(
    () => (
      <>
        <FilterFieldSelect
          id="users-filter-role"
          label="Role"
          value={role}
          onChange={(e) => setRoleFilter(e.target.value)}
          options={ROLE_OPTIONS}
        />
        <FilterFieldSelect
          id="users-filter-status"
          label="Status"
          value={status}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={STATUS_OPTIONS}
        />
      </>
    ),
    // searchParamsKey keeps URL-driven handlers in sync; value props track filter state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, status, searchParamsKey]
  );

  return (
    <>
      <ObjectTopBar
        title="Users"
        primaryBtn={canInvite ? {
          btnText: 'Invite User',
          btnIcon: 'bi bi-person-plus',
          variant: 'primary',
          onClick: () => setShowInvite(true),
        } : undefined}
      />
      <div className="container py-4">
        {/* Search/filter handled inside DataTable */}
        {loading && users.length === 0 ? (
          <LoadingView
            symbolClass="bi-people"
            title="Loading users"
            subtitle="Fetching team members for your organization."
            className="py-5"
          />
        ) : (
          <>
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2" />
                {error}
              </div>
            )}
            <UsersTable
              items={users}
              filterExtras={filterExtras}
              onEditRole={setEditUser}
              onResendInvite={handleResendInvite}
              onDeactivate={openDeactivate}
              onReactivate={openReactivate}
            />
          </>
        )}
        <InviteUserModal show={showInvite} onClose={() => setShowInvite(false)} onInvited={fetchUsers} />
        <EditUserModal
          show={Boolean(editUser)}
          onClose={() => setEditUser(null)}
          actor={currentUser}
          user={editUser}
          onUpdated={fetchUsers}
        />
        <DeactivateUserModal
          show={Boolean(toggleUser)}
          onClose={() => setToggleUser(null)}
          user={toggleUser}
          action={toggleAction}
          onCompleted={fetchUsers}
        />
      </div>
    </>
  );
}
